import SheetDialog, { SheetDialogActions, SheetDialogButton, SheetDialogControls, SheetDialogText } from "@/components/common/elements/SheetDialog";
import { FORM_COLOR } from "@/pages/Seminar/components/form/formStyles";

// 모달 문구를 문장 단위로 줄바꿈 (한국어 문장 끝 + 공백 → 개행). 의도적 개행은 보존.
const breakSentences = (text?: string): string | undefined =>
    typeof text === "string" ? text.replace(/([다요][.!?]) +/g, "$1\n") : text;

/**
 * 예전에 위험 동작 버튼에 넘기던 빨강들 — 가이드 1장의 빨강(#E11D48) 하나로 그린다.
 * 화면 파일이 아직 옛 값을 넘겨도(공고 삭제 #dc2626 등) 창에서는 같은 빨강이 나온다.
 */
const LEGACY_DANGER_COLORS: ReadonlySet<string> = new Set([
    "#dc2626", "#ef4444", "#b91c1c", "#b83a2e", "#d32f2f", "#e53935", "#f44336", "#ff4d4f", "red",
    FORM_COLOR.danger.toLowerCase(),
]);

interface ConfirmModalProps {
    title: string;
    description?: string;
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    confirmText?: string;
    cancelText?: string;
    /** 확인 버튼 색 — 빨강 계열을 넘기면 위험 동작으로 보고 #E11D48 로 그린다. 새 코드는 [danger] 를 쓴다 */
    confirmColor?: string;
    /** 위험 동작(삭제·차단·탈퇴 등) — 확인 버튼을 빨강(#E11D48)으로 */
    danger?: boolean;
    /** 예전 고정 높이 — 창 높이는 내용에 맞추므로 쓰지 않는다(넘겨도 무시, 호출부 호환용) */
    height?: string;
    /**
     * 몸통 스크롤 잠금(body overflow:hidden)을 끈다 — html 이 overflow:clip 이라 잠금이 걸리면 body 가 잘림 상자가 되어
     * sticky 머리·입력줄이 풀린다(지원 대화방 위 바가 사라지고 입력줄 아래가 빈 사고, 2026-10-06). 기본은 잠금(기존 그대로).
     * PC 작은 창·폰 시트 둘 다 지킨다(SheetDialog).
     */
    disableScrollLock?: boolean;
}

/**
 * 확인 창 — 폰(≤768px)은 아래 시트, PC 는 가운데 작은 창(공용 SheetDialog, UI 가이드 5장).
 * 버튼 52px·반경 14(가이드 4장): [취소] 흰 바탕 테두리 · [확인] 청록, 위험 동작은 빨강 #E11D48 하나.
 * 확인을 누르면 창을 닫은 뒤(폰은 쌓은 방문 기록 칸을 먼저 되돌린 뒤) onConfirm → onClose 순서로 부른다 —
 * onConfirm 안에서 다른 화면으로 이동해도 되돌려지지 않는다.
 */
const ConfirmModal = ({
    title,
    description,
    open,
    onClose,
    onConfirm,
    confirmText = "확인",
    cancelText = "취소",
    confirmColor,
    danger = false,
    disableScrollLock = false,
}: ConfirmModalProps) => {
    const isDanger: boolean = danger || (confirmColor != null && LEGACY_DANGER_COLORS.has(confirmColor.toLowerCase()));

    return (
        <SheetDialog
            open={open}
            title={breakSentences(title)}
            onClose={onClose}
            disableScrollLock={disableScrollLock}
            footer={(controls: SheetDialogControls) => (
                <SheetDialogActions>
                    <SheetDialogButton type="button" onClick={controls.close}>
                        {cancelText}
                    </SheetDialogButton>
                    <SheetDialogButton
                        type="button"
                        tone={isDanger ? "danger" : "primary"}
                        accent={isDanger ? undefined : confirmColor}
                        onClick={() =>
                            controls.done(() => {
                                onConfirm();
                                onClose();
                            })
                        }
                    >
                        {confirmText}
                    </SheetDialogButton>
                </SheetDialogActions>
            )}
        >
            {description && <SheetDialogText>{breakSentences(description)}</SheetDialogText>}
        </SheetDialog>
    );
};

export default ConfirmModal;
